import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../../config/constant';
import { getToken, getRole, getUserName } from '../../../Service/Service';
import { IStandardAPIResponse } from '../../../definitions/resp';

interface IAgent {
  agent: string;
  agent_photo: string;
  totalConnection: number;
  load: number;
  installationComplete: number;
  regulator: number;
  pipe: number;
  paidLight: number;
  nonHpOven: number;
  hpOven: number;
  bplOven: number;
  paidAmount: number;
  remarks: string;
}

interface IPricing {
  hpOvenPricing: number;
  nonHpOvenPricing: number;
}

interface ISMSData {
  agent: string;
  load: number;
  regulator: number;
  pipe: number;
  light: number;
  bplOven: number;
  nonHpOven: number;
  hpOven: number;
  installationPending: number;
  paidAmount: number;
  dueAmount: number;
}

export const useConnection = () => {
  const [isLoading, setLoading] = useState(false);
  const [agent, setAgent] = useState<IAgent>({} as IAgent);
  const [pricing, setPricing] = useState<IPricing>({} as IPricing);
  const [openPrice, setOpenPrice] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [data, setData] = useState([]);
  const [sms, setSms] = useState<ISMSData>({
    agent: '',
    load: 0,
    regulator: 0,
    pipe: 0,
    light: 0,
    bplOven: 0,
    nonHpOven: 0,
    hpOven: 0,
    installationPending: 0,
    paidAmount: 0,
    dueAmount: 0,
  });

  const columns = [
    { field: 'agent', title: 'AGENT' },
    { field: 'totalConnection', title: 'Connection' },
    { field: 'load', title: 'Load Paid' },
    { field: 'regulator', title: 'Regulator Paid' },
    { field: 'pipe', title: 'Pipe Paid' },
    { field: 'paidLight', title: 'LIGHT Paid' },
    { field: 'bplOven', title: 'BPL Oven' },
    { field: 'nonHpOven', title: 'Non HP Oven' },
    { field: 'hpOven', title: 'HP Oven' },
    { field: 'paidAmount', title: 'Paid Amount' },
    { field: 'remarks', title: 'REMARKS' },
    { field: 'updatedAt', title: 'DATE' },
  ];

  const fetchPricing = async () => {
    try {
      const result = await axios.get<IStandardAPIResponse<IPricing>>(`${BASE_URL}pricing/get`, {
        headers: {
          encryption: false,
          token: getToken(),
        },
      });
      if (result?.data) {
        setPricing(result.data.data[0]);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleChangeSMS = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSms((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const sendSms = async () => {
    try {
      const result = await axios.post(
        `${BASE_URL}agent/sendsms`,
        { sms },
        {
          headers: {
            encryption: false,
            token: getToken(),
          },
        }
      );
      return result.data;
    } catch (error) {
      console.error('Error sending SMS:', error);
    }
  };

  const fetchAgentData = async (agentName: string) => {
    setLoading(true);
    try {
      const result = await axios.post(
        `${BASE_URL}agent/connection/get`,
        { agent: getRole() !== 'user' ? agentName : getUserName() },
        {
          headers: {
            encryption: false,
            token: getToken(),
          },
        }
      );
      if (result?.data) {
        setAgent(result.data.data);
      }
    } catch (error) {
      console.error('Error fetching agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesHistory = async (agentName: string) => {
    setLoadingTable(true);
    try {
      const result = await axios.post(
        `${BASE_URL}agent/slaes/getall`,
        { agent: getRole() !== 'user' ? agentName : getUserName() },
        {
          headers: {
            encryption: false,
            token: getToken(),
          },
        }
      );
      if (result?.data) {
        setData(result.data.data);
      }
    } catch (error) {
      console.error('Error fetching sales history:', error);
    } finally {
      setLoadingTable(false);
    }
  };

  const fetchAgentList = useCallback(async () => {
    try {
      const result = await axios.get(`${BASE_URL}agent/getall/active`, {
        headers: {
          encryption: false,
          token: getToken(),
        },
      });
      return result.data.data.agents.map(({ name }: { name: string }) => ({
        label: name,
        value: name,
      }));
    } catch (error) {
      console.error('Error fetching agents:', error);
      return [];
    }
  }, []);

  const updateAgentConnection = async (updatedAgent: IAgent) => {
    try {
      const result = await axios.post(`${BASE_URL}agent/connection/update`, updatedAgent, {
        headers: {
          encryption: false,
          token: getToken(),
        },
      });
      return result.data;
    } catch (error) {
      console.error('Error updating agent:', error);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  return {
    isLoading,
    loadingTable,
    agent,
    pricing,
    sms,
    data,
    columns,
    open,
    openPrice,
    setOpen,
    setOpenPrice,
    handleChangeSMS,
    sendSms,
    fetchAgentData,
    fetchSalesHistory,
    fetchPricing,
    fetchAgentList,
    setAgent,
    updateAgentConnection,
  };
};
